import { isEmpty } from "lodash";
import { useCallback, useMemo, useRef } from "react";
import { clear, getValue, setReadMode } from "e3-common/utils/controlsUtil";
import { validate } from "e3-common/utils/validationUtil";

/**
 * Provide some useful functions to interact with form
 *
 * @param {*} formControl Form control (should add new properties by each control item)
 * For example with the field [txtID]:
 *    [formField]: Mapping with database field name
 *    [isKey]: If true => specify the field control is the key in form
 *
 * @param {*} options Options
 */
export const useHookForm = (formControl = {}, options = {}) => {
  /**
   * disableKeyIfInitEdit: Disabled key field when initial form with the key has value
   */
  const { disableKeyIfInitEdit } = options || {};

  const formValuesRef = useRef({}); // store form values at the time call reset()

  // Mapping by formField (database field) with field ref config by form control
  // For example: { ID: [txtIdRef], NAME: [txtNameRef] }
  const mappingControls = useMemo(() => {
    return Object.values(formControl || {}).reduce((acc, { formField, ref }) => ({ ...acc, [formField]: ref }), {});
  }, [formControl]);

  /**
   * Get the key field config by form control
   */
  const getKeyFieldConfigs = useCallback(() => {
    const keyFieldConfigs = Object.values(formControl).find(({ isKey }) => !!isKey) || {};
    const { ref, formField } = keyFieldConfigs;

    return {
      ...keyFieldConfigs,
      keyRef: ref?.current || {},
      keyValue: formValuesRef.current[formField],
    };
  }, [formControl]);

  /**
   * Clear form data
   */
  const clearForm = useCallback(() => {
    clear(formControl, true);
    formValuesRef.current = {};
  }, [formControl]);

  /**
   * Set values into form by mapping formField inside form control
   */
  const setValues = useCallback(
    (values) => {
      Object.entries(values || {}).forEach(([key, value]) => {
        mappingControls[key]?.current?.setValue(value);
      });
    },
    [mappingControls]
  );

  /**
   * Get form values
   */
  const getValues = useCallback(
    (queryString = false, camelCase = false) => {
      return getValue(formControl, queryString, camelCase);
    },
    [formControl]
  );

  /**
   * Reset form with default values
   * If we don't pass the values, the form will be clean (as same as clearForm())
   * The values will be stored
   */
  const reset = useCallback(
    (values, options) => {
      if (isEmpty(values)) return clearForm();

      formValuesRef.current = values;
      setValues(values);

      const { disableKey } = options || {};
      const { keyRef, keyValue } = getKeyFieldConfigs();
      // Disable key when initial form in edit mode (has key value) or specify disableKey when setValues
      const isDisabledKey = disableKey || (disableKeyIfInitEdit && !!keyValue);

      if (isDisabledKey) keyRef?.disable();
    },
    [mappingControls]
  );

  /**
   * Restore form values at the time call reset()
   */
  const restore = useCallback(
    (options) => {
      reset(formValuesRef.current, options);
    },
    [mappingControls]
  );

  /**
   * Validate form data by form control
   */
  const validateForm = useCallback(() => validate(formControl), [formControl]);

  /**
   * Toggle the entire form control to read-only mode or not.
   */
  const readOnlyMode = useCallback(
    (readOnly) => {
      setReadMode(formControl, readOnly);
    },
    [formControl]
  );

  /**
   * Disable / Enable form fields
   */
  const toggleDisableFields = useCallback(
    (options) => {
      const { isDisabled, except } = options || {};

      Object.values(formControl || {}).forEach(({ formField, ref }) => {
        if (except?.includes(formField)) return;

        isDisabled ? ref?.current?.disable?.() : ref?.current?.enable?.();
      });
    },
    [formControl]
  );

  return {
    getValues,
    reset,
    restore,
    setValues,
    validateForm,
    toggleDisableFields,
    readOnlyMode,
  };
};
